import { FC, lazy } from 'react';
import { ShipperTransactionsParams } from 'src/apis/capacity-management/types';
import { useCapacityCategories } from 'src/apis/capacity-management/use-capacity-categories';
import { useCapacityManagementRoot } from 'src/apis/capacity-management/use-capacity-management-root';
import { useMarketAreas } from 'src/apis/capacity-management/use-market-areas';
import { useShipperTransactions } from 'src/apis/capacity-management/use-shipper-transactions';
import { Stack } from 'src/components/layout/stack';
import { PageSpinner } from 'src/components/spinner-container';
import { Heading } from 'src/components/text/heading';
import { usePageParams } from 'src/pages/reporting/shipper-transactions/use-page-params';
import { useMemoOne } from 'use-memo-one';

const TransactionsPage = lazy(() => import('./page'));

export const ShipperTransactions: FC = () => {
  const capacityManagementRoot = useCapacityManagementRoot();
  const capacityCategories = useCapacityCategories({ capacityManagementRoot });
  const marketAreas = useMarketAreas({ capacityManagementRoot });

  const pageParams = usePageParams();

  const params = useMemoOne<ShipperTransactionsParams>(() => {
    const {
      start,
      pageSize,
      networkPointId,
      shipperId,
      transactionTypeSpecificId,
      ...rest
    } = pageParams.value;
    return {
      offset: start,
      limit: pageSize,
      networkPointId: networkPointId.map((id) => id.value),
      shipperId: shipperId.map((id) => id.value),
      transactionTypeSpecificId: transactionTypeSpecificId
        ? [transactionTypeSpecificId]
        : [],
      ...rest,
    };
  }, [pageParams.value]);

  const shipperTransactions = useShipperTransactions({
    capacityManagementRoot,
    params,
  });

  if (!capacityManagementRoot.response) return <PageSpinner />;

  // version 2 of api adds Direct FCFS to the transactions table
  const isV2 = capacityManagementRoot.response.data._links?.myTsoTransactionsV2;

  return (
    <Stack gap={2}>
      <Heading mode="title">Shipper Transactions</Heading>

      <TransactionsPage
        capacityManagementRoot={capacityManagementRoot}
        shipperTransactions={shipperTransactions}
        pageParams={pageParams}
        capacityCategories={capacityCategories}
        marketAreas={marketAreas}
        isV2={Boolean(isV2)}
      />
    </Stack>
  );
};
